import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../core/config/environment";
import { Area, AssertionIssue, db, ImageType, InspectionJob, JobIssue, JobIssueVendor, Media, PostCompleteStatus, SyncableStatus, UserVendorContact } from "../../../core/database/db";
import { UserVendorContactDTO } from "../../vendors/models/UserVendorContactDTO";
import { NetworkStatusService } from "../../../shared/services/network-status.service";
import DataSyncService from "../../../core/sync/data-sync/data-sync.service";
import { JobWithLot } from "../../jobs/models/JobWithLot";
import { LoggerService } from "../../../shared/services/logger.service";
import { MediaService } from "../../media/services/media.service";
import { determinVendorAssignmentStatesForIssue, JobIssueVendorService } from "./job-issue-vendor.service";
import { v4 as uuidv4 } from "uuid";
import { JobIssueVendorWithDetails, JobIssueWithDetails } from "../models/JobIssueWithDetails";
import { NO_VENDOR_ASSIGNED_IDENTIFIER, SELF_ASSIGNED_VENDOR_ASSIGNMENT, UNKNOWN_VENDOR, VendorDTO } from "../../vendors/models/VendorDTO";
import { MediaForCompression } from "../../media/services/image-compression-pipeline.service";
import { UploadManagerService } from "../../../core/sync/image-sync/upload-manager.service";
import DataSyncMapperService from "../../../core/sync/data-sync/data-sync-mapper.service";
import { AccumulatedResults, IssueSyncResponse } from "../../../core/sync/models/SyncResults.model";

export interface GetConstructionManagerJobScheduleResponse {
  jobs?: JobWithLot[];
  userVendorContacts: UserVendorContactDTO[];
}

@Injectable({ providedIn: "root" })
export class JobIssueService {

  private readonly sendIssuesToVendorsUrl =
    `${environment.backend.apiBaseURL}/api/Vendor/SendIssuesToVendors`;

  isOnline: boolean = true;

  constructor(private http: HttpClient,
    private logger: LoggerService,
    private networkService: NetworkStatusService,
    private syncService: DataSyncService,
    private mediaService: MediaService,
    private dataSyncMapperService: DataSyncMapperService,
    private dataSyncService: DataSyncService,
    private jobIssueVendorService: JobIssueVendorService,
    private uploadManagerService: UploadManagerService
  ) {

    this.networkService.online$.subscribe(val => {
      this.isOnline = val;
    });
  }

  public async sendIssuesToVendors(
    lotId: number,
    externalVendorId: number,
    jobIssues: JobIssue[],
    vendorAssignments: JobIssueVendor[],
    userVendorContacts: UserVendorContact[]
  ): Promise<JobIssueVendor[]> {
    try {
      if (this.isOnline) {
        this.logger.event("JobIssueService:SendingIssuesToVendors", { lotId, externalVendorId, issueCount: jobIssues.length });

        const jobIssuesToSend = jobIssues.map(i => this.dataSyncMapperService.mapJobIssue(i, [], vendorAssignments));
        const contactsToSend = userVendorContacts.map(c => this.dataSyncMapperService.mapVendorContact(c));

        const sendResponse = await firstValueFrom(
          this.http.post<IssueSyncResponse>(`${this.sendIssuesToVendorsUrl}/${lotId}/${externalVendorId}`, {
            JobIssues: jobIssuesToSend,
            UserVendorContacts: contactsToSend
          })
        );

        if (sendResponse) {
          const sentAt = new Date();
          const vendorAssignmentsToUpdate = vendorAssignments.map<JobIssueVendor>(assignment => ({
            ...assignment,
            isSent: true,
            lastSentAt: sentAt,
            viewedAt: undefined
          }));

          await db.jobIssueVendor.bulkUpdate(vendorAssignmentsToUpdate.map(assignment => ({
            key: assignment.id!,
            changes: {
              isSent: assignment.isSent,
              lastSentAt: assignment.lastSentAt,
              viewedAt: assignment.viewedAt
            }
          })));

          const syncApiResults: AccumulatedResults = {
            jobIds: [],
            areaIdentifiers: [],
            componentIdentifiers: [],
            assertionIdentifiers: [],
            issueIdentifiers: [],
            mediaIdentifiers: [],
            contactResultSet: [],
            signatureIdentifiers: [],
            jobIssueIdentifiers: [],
            jobIssueMediaIdentifiers: [],
            jobIssueVendorIdentifiers: [],
            deletedJobIssueVendorIdentifiers: [],
            deletedJobIssueMediaIdentifiers: [],
            deletedUserVendorContactIdentifiers: [],
            deletedAssertionIssueIdentifiers: [],
            deletedAssertionMediaIdentifiers: [],
            deletedJobIssueIdentifiers: [],
            lotAssetMediaIdentifiers: [],
            deletedLotAssetMediaIdentifiers: [],
            alreadyCompletedJobIds: []
          };

          await this.syncService.updateSyncedDatabaseEntitiesWithResults(this.dataSyncService.mergeSyncResults(syncApiResults, sendResponse));

          return vendorAssignmentsToUpdate;
        }
      }
    } catch (error) {
      this.logger.error("Failed to send issues to vendors", error);
    }

    return [];
  }

  public getJobIssuesForJob(jobId: number, includeDeleted = false) {
    return db.jobIssue
      .where("jobId")
      .equals(jobId)
      .and(issue => includeDeleted || !issue.deleted)
      .toArray();
  }

  public async getJobIssuesForJobs(jobIds: number[], includeDeleted = false) {
    return await db.jobIssue
      .where("jobId")
      .anyOf(jobIds)
      .and(issue => includeDeleted || !issue.deleted)
      .toArray();
  }

  public async hasAnyIssueForJob(jobId: number): Promise<boolean> {
    const issue = await db.jobIssue.where("jobId").equals(jobId)
      .first();

    return !!issue;
  }

  /**
   * Returns the active (non-deleted) job issues for the given jobs, excluding any issue whose identifier is referenced as
   * another issue's parentIdentifier. When a parent CCM issue is converted into a CM child issue,
   * both records exist for the lot; without this filter the parent and its child would both appear
   * on the CM's Open/Completed Items lists as duplicates. Keeping only "leaf" issues shows each
   * item once (its most-derived version).
   */
  public async getLeafJobIssuesForJobs(jobIds: number[]): Promise<JobIssue[]> {
    const activeIssues = await this.getJobIssuesForJobs(jobIds);

    const identifiersWithChildren = new Set(
      activeIssues
        .map(issue => issue.parentIdentifier)
        .filter((parentIdentifier): parentIdentifier is string => !!parentIdentifier)
    );

    return activeIssues.filter(issue => !identifiersWithChildren.has(issue.identifier));
  }

  /**
   * Returns the freshest active vendor assignments for the given issues, re-keyed to the requested
   * identifier.
   */
  public async getLatestVendorAssignmentsForIssues(issueIdentifiers: string[]): Promise<JobIssueVendor[]> {
    if (issueIdentifiers.length === 0) {
      return [];
    }

    const childIssues = await db.jobIssue
      .where("parentIdentifier")
      .anyOf(issueIdentifiers)
      .and(issue => !issue.deleted)
      .toArray();

    const sourceIdentifierByChildIdentifier = new Map(
      childIssues.map(child => [child.identifier, child.parentIdentifier!])
    );

    const assignments = await this.jobIssueVendorService.getAllActiveVendorsForIssues([
      ...issueIdentifiers,
      ...sourceIdentifierByChildIdentifier.keys()
    ]);

    const fromChildren = this.groupAssignmentsBySourceIssue(
      assignments.filter(assignment => sourceIdentifierByChildIdentifier.has(assignment.issueIdentifier)),
      assignment => sourceIdentifierByChildIdentifier.get(assignment.issueIdentifier)!
    );
    const fromOriginals = this.groupAssignmentsBySourceIssue(
      assignments.filter(assignment => !sourceIdentifierByChildIdentifier.has(assignment.issueIdentifier)),
      assignment => assignment.issueIdentifier
    );

    return issueIdentifiers.flatMap(identifier =>
      fromChildren.get(identifier) ?? fromOriginals.get(identifier) ?? []
    );
  }

  /**
   * Returns the active vendor assignments for the given issues keyed by issue identifier
   */
  public async getVendorAssignmentsByIssueIdentifier(
    issues: Array<{ identifier: string; supersededIdentifiers?: string[] }>
  ): Promise<Map<string, JobIssueVendor[]>> {
    if (issues.length === 0) {
      return new Map();
    }

    const issueIdentifierBySupersededIdentifier = new Map<string, string>();
    issues.forEach(issue =>
      (issue.supersededIdentifiers ?? []).forEach(superseded =>
        issueIdentifierBySupersededIdentifier.set(superseded, issue.identifier)));

    const assignments = await this.jobIssueVendorService.getAllActiveVendorsForIssues([
      ...issues.map(issue => issue.identifier),
      ...issueIdentifierBySupersededIdentifier.keys()
    ]);

    const fromSuperseded = this.groupAssignmentsBySourceIssue(
      assignments.filter(assignment => issueIdentifierBySupersededIdentifier.has(assignment.issueIdentifier)),
      assignment => issueIdentifierBySupersededIdentifier.get(assignment.issueIdentifier)!
    );
    const fromIssues = this.groupAssignmentsBySourceIssue(
      assignments.filter(assignment => !issueIdentifierBySupersededIdentifier.has(assignment.issueIdentifier)),
      assignment => assignment.issueIdentifier
    );

    return new Map(issues.map(issue => [
      issue.identifier,
      fromIssues.get(issue.identifier) ?? fromSuperseded.get(issue.identifier) ?? []
    ]));
  }

  private groupAssignmentsBySourceIssue(
    assignments: JobIssueVendor[],
    resolveSourceIdentifier: (assignment: JobIssueVendor) => string
  ): Map<string, JobIssueVendor[]> {
    return assignments.reduce((map, assignment) => {
      const sourceIdentifier = resolveSourceIdentifier(assignment);
      map.set(sourceIdentifier, [
        ...(map.get(sourceIdentifier) ?? []),
        { ...assignment, issueIdentifier: sourceIdentifier }
      ]);
      return map;
    }, new Map<string, JobIssueVendor[]>());
  }

  public async deleteIssueWithRelatedRecords(issue: JobIssue): Promise<void> {
    if (!issue.id) {
      throw new Error("Cannot delete issue without an id.");
    }

    await db.transaction("rw", [db.jobIssue, db.media, db.jobIssueVendor, db.syncStatus], async () => {
      await this.softDeleteJobIssue(issue.id!);
      await this.mediaService.softDeleteMediaForParent(issue.identifier);
      await this.jobIssueVendorService.persistVendorForIssue(issue.identifier, null);
    });
  }

  public async confirmIssueWithHomeowner(jobIssue: JobIssue): Promise<void> {
    if (jobIssue.id) {
      await db.jobIssue.update(jobIssue.id, {
        isConfirmedByHomeowner: true,
        confirmedByHomeownerAt: new Date(),
        updatedAt: new Date(),
        syncStatus: SyncableStatus.Pending
      });
      jobIssue.isConfirmedByHomeowner = true;
      jobIssue.confirmedByHomeownerAt = new Date();
    } else {
      this.logger.warn("Attempted to confirm issue with homeowner, but issue has no id", jobIssue);
    }
  }

  public async markIssueNotComplete(jobIssue: JobIssue, postCompleteStatus: PostCompleteStatus, text: string): Promise<void> {
    if (jobIssue.id) {
      await db.jobIssue.update(jobIssue.id, {
        postCompleteStatus,
        text,
        updatedAt: new Date(),
        syncStatus: SyncableStatus.Pending,
        isConfirmedByHomeowner: false,
        confirmedByHomeownerAt: undefined
      });

      await db.media
        .where("parentIdentifier")
        .equals(jobIssue.identifier)
        .and(m => m.imageType === ImageType.ConfirmationHomeowner && !m.deleted)
        .modify({ deleted: 1 });
    } else {
      this.logger.warn("Attempted to mark issue not complete, but issue has no id", jobIssue);
    }
  }

  public async addJobIssue(issue: Omit<JobIssue, "id">): Promise<number> {
    return db.jobIssue.add({
      ...issue,
      syncStatus: SyncableStatus.Pending,
      updatedAt: new Date()
    });
  }

  public async markIssueComplete(issueId: number): Promise<void> {
    const now = new Date();
    await db.jobIssue.update(issueId, {
      isComplete: true,
      completedAt: now,
      updatedAt: now
    });
  }

  public async assignRoomToIssue(issueId: number, lotAreaIdentifier: string): Promise<void> {
    await db.jobIssue.update(issueId, {
      lotAreaIdentifier,
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending
    });
  }

  public async updateIssueText(issueId: number, text: string): Promise<void> {
    await db.jobIssue.update(issueId, {
      text,
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending
    });
  }

  public async saveJobIssue(
    issue: Omit<JobIssue, "id">,
    media: Media[],
    externalVendorId: number | null,
    vendorName?: string
  ): Promise<JobIssue> {
    await this.mediaService.bulkAddMedia(media);
    const newId = await this.addJobIssue(issue);
    const savedIssue: JobIssue = { ...issue, id: newId };

    if (externalVendorId !== null) {
      await this.jobIssueVendorService.persistVendorForIssue(
        savedIssue.identifier,
        externalVendorId,
        vendorName
      );
    }

    return savedIssue;
  }

  public async resetIssueConfirmation(issueId: number): Promise<void> {
    await db.jobIssue.update(issueId, {
      isConfirmedByHomeowner: false,
      confirmedByHomeownerAt: undefined,
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending
    });
  }

  public async softDeleteJobIssue(issueId: number): Promise<void> {
    await db.jobIssue.update(issueId, {
      deleted: 1,
      deletedAt: new Date(),
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending
    });
  }

  public async updateJobIssue(id: number, changes: Partial<Omit<JobIssue, "id">>, media: Media[] = []): Promise<Record<string, number>> {
    let addedMediaIds: number[] = [];

    const mediaToDelete = media.filter(m => m.deleted === 1);
    const newMedia = media.filter(m => !m.id);

    const mediaToSave: Media[] = newMedia.map(img => {
      const { file: _f, ...rest } = img as MediaForCompression;
      return rest;
    });

    await db.transaction("rw", [db.jobIssue, db.media, db.syncStatus], async () => {
      await db.jobIssue.update(id, {
        ...changes,
        syncStatus: SyncableStatus.Pending,
        updatedAt: new Date()
      });
      if (mediaToSave.length > 0) {
        addedMediaIds = await db.media.bulkAdd(mediaToSave, { allKeys: true });
      }
      if (mediaToDelete.length > 0) {
        const mediaIdsToDelete = mediaToDelete.map(m => m.id).filter(id => id !== undefined) as number[];
        await db.softDeleteMedia(mediaIdsToDelete);
      }
    });
    await this.markMediaReadyForUpload(addedMediaIds);

    const mediaFileIdentifierToIdMap = mediaToSave.reduce((acc, media, index) => {
      const mediaId = addedMediaIds[index];
      if (mediaId) {
        acc[media.fileIdentifier] = mediaId;
      }
      return acc;
    }, {} as Record<string, number>);

    return mediaFileIdentifierToIdMap;
  }

  public async getJobIssueByIdentifier(identifier: string): Promise<JobIssue | undefined> {
    return db.jobIssue
      .where("identifier")
      .equals(identifier)
      .and(issue => !issue.deleted)
      .first();
  }

  public async cloneAssertionIssuesToChildJobIssues(issuesToClone: AssertionIssue[], updatedAt: Date): Promise<void> {
    const alreadyClonedParentIdentifiers = new Set(
      (
        await db.jobIssue
          .where("parentIdentifier")
          .anyOf(issuesToClone.map(i => i.identifier))
          .toArray()
      )
        .map(issue => issue.parentIdentifier)
        .filter((parentIdentifier): parentIdentifier is string => !!parentIdentifier)
    );

    // Skip CCM issues already cloned on the device (a child JobIssue exists for them), and de-dupe
    // any CCM issue that appears more than once within this same input batch.
    const queuedParentIdentifiers = new Set<string>();
    const issuesToAdd = issuesToClone.filter(issue => {
      if (alreadyClonedParentIdentifiers.has(issue.identifier) || queuedParentIdentifiers.has(issue.identifier)) {
        return false;
      }
      queuedParentIdentifiers.add(issue.identifier);
      return true;
    });

    const stagedIssues = await Promise.all<JobIssue>(issuesToAdd.map(async (i: AssertionIssue) => {
      let lotAreaIdentifier: string | undefined;
      try {
        const assertion = await db.assertion.where("identifier").equals(i.assertionIdentifier)
          .first();
        if (assertion) {
          const component = await db.component.get(assertion.componentId);
          if (component) {
            const area = await db.area.get(component.areaId);
            if (area) {
              lotAreaIdentifier = area.identifier;
            }
          }
        }
      } catch (error) {
        this.logger.error(`Error resolving area for issue ${i.id}`, error);
      }

      return {
        ...i,
        identifier: uuidv4(),
        parentIdentifier: i.identifier,
        lotAreaIdentifier,
        dueDate: this.calculateDueDate(updatedAt)
      };
    }));

    await db.batchBulkOperation("cloneAssertionIssuesToChildJobIssues", stagedIssues, async batchItem => {
      await db.jobIssue.bulkAdd(batchItem.map(i => ({
        ...i,
        syncStatus: SyncableStatus.Pending,
        id: undefined
      })));
    }, this.logger);

    // Carry the trade partner (and its sent/viewed delivery state) onto the child so a confirmed
    // carryover keeps the vendor history the CM already sees on their open items list.
    await this.jobIssueVendorService.cloneVendorAssignmentsForIssues(
      stagedIssues.map(i => ({
        sourceIssueIdentifier: i.parentIdentifier!,
        targetIssueIdentifier: i.identifier
      })),
      updatedAt
    );

    const medias = await db.media
      .where("parentIdentifier")
      .anyOf(stagedIssues.map(i => i.parentIdentifier!))
      .toArray();

    // Create new media records for job issues (duplicate existing ones)
    const newMediaRecords = medias.map(media => {
      const matchingStagedIssue = stagedIssues.filter(
        i => i.parentIdentifier === media.parentIdentifier
      )[0];

      return {
        ...media,
        id: undefined, // Let database assign new ID
        parentIdentifier: matchingStagedIssue.identifier, // Link to JobIssue
        syncStatus: SyncableStatus.Pending,
        updatedAt: new Date()
      };
    });

    await db.batchBulkOperation("cloneAssertionIssuesToChildJobIssuesMedia", newMediaRecords, batchItem => db.media.bulkAdd(batchItem), this.logger);
  }

  public createIssueWithDetails(issue: JobIssue, areaMap: Map<string, Area>, media: Media[], assertionIssues: AssertionIssue[], jobs: InspectionJob[], jobIssueVendors: JobIssueVendor[], allVendors: VendorDTO[], userVendorContacts: UserVendorContact[], allJobIssuesForLot: JobIssue[], shouldProcessAncestors = true): JobIssueWithDetails {
    const areaForIssue = issue.lotAreaIdentifier ? areaMap.get(issue.lotAreaIdentifier)?.name : undefined;

    const mediaForIssue = media.filter(m => m.parentIdentifier === issue.identifier);
    const jobForIssue = jobs.find(j => j.id === issue.jobId);
    const vendorAssignmentsForIssue = jobIssueVendors.filter(v => v.issueIdentifier === issue.identifier);

    const vendorDeliveryStates = determinVendorAssignmentStatesForIssue(issue, vendorAssignmentsForIssue, userVendorContacts);
    const assignedVendorsForIssue = vendorAssignmentsForIssue
      .map(jobIssueVendor => {
        const vendorForAssignedVendor = allVendors.find(v => v.id === jobIssueVendor.externalVendorId) ?? UNKNOWN_VENDOR;

        const contactsForVendor = userVendorContacts
          .filter(uvc => uvc.externalVendorId === vendorForAssignedVendor.id)
          .sort((a, b) => a.email.localeCompare(b.email));

        const vendorDeliveryState = vendorDeliveryStates[jobIssueVendor.issueVendorIdentifier]
          ?? vendorDeliveryStates[SELF_ASSIGNED_VENDOR_ASSIGNMENT.issueIdentifier]
          ?? vendorDeliveryStates[NO_VENDOR_ASSIGNED_IDENTIFIER];

        const vendorWithDetails: JobIssueVendorWithDetails = {
          ...jobIssueVendor,
          vendorDetails: {
            ...vendorForAssignedVendor,
            userVendorContacts: contactsForVendor
          },
          vendorDeliveryState
        };

        return vendorWithDetails;
      });

    const relatedAssertionIssue = assertionIssues.find(ai => ai.identifier === issue.identifier);
    const ancestorJobIssues = shouldProcessAncestors ? this.flattenAncestorIssues(issue, allJobIssuesForLot) : [];

    return {
      ...issue,
      areaName: areaForIssue,
      isAreaUnassigned: !issue.lotAreaIdentifier || issue.lotAreaIdentifier.trim().length === 0,
      buildQualityStep: jobForIssue?.buildQualityStep,
      assignedVendors: assignedVendorsForIssue,
      media: mediaForIssue,
      relatedAssertionIssue,
      ancestorJobIssues: ancestorJobIssues.map(ancestor => this.createIssueWithDetails(ancestor, areaMap, media, assertionIssues, jobs, jobIssueVendors, allVendors, userVendorContacts, allJobIssuesForLot, false))
    };
  }

  private calculateDueDate(start: Date): Date {
    const dueDate = new Date(start);
    let weekdaysAdded = 0;
    while (weekdaysAdded < 2) {
      dueDate.setDate(dueDate.getDate() + 1);
      const dayOfWeek = dueDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        weekdaysAdded += 1;
      }
    }
    return dueDate;
  }

  public async assignIssueToMe(issue: JobIssue) {
    return this.updateJobIssue(issue.id!, {
      isSelfAssigned: true,
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending
    });
  }

  public async unassignIssueFromMe(issue: JobIssue) {
    return this.updateJobIssue(issue.id!, {
      isSelfAssigned: false,
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending
    });
  }

  private async markMediaReadyForUpload(addedMediaIds: number[]): Promise<void> {
    if (addedMediaIds.length > 0) {
      await this.uploadManagerService.markFilesReadyForUpload(addedMediaIds);
    }
  }

  private flattenAncestorIssues(issue: JobIssue, allJobIssuesForLot: JobIssue[]): JobIssue[] {
    const ancestors: JobIssue[] = [];
    let currentIssue = issue;
    while (currentIssue.parentIdentifier) {
      const parentIdentifier = currentIssue.parentIdentifier;
      const parent = allJobIssuesForLot.find(j => j.identifier === parentIdentifier);
      if (!parent) {
        break;
      }
      ancestors.push(parent);
      currentIssue = parent;
    }
    return ancestors;
  }
}